import { useEffect, useRef } from 'react';
import Button from '@/components/Common/Button';
import DataTable from '@/components/Common/DataTable';
import Select from '@/components/Common/Select';
import QuickActionIcon from '@/assets/icons/QuickActionIcon';
import AutomationIcon from '@/assets/icons/AutomationIcon';
import SyncDbIcon from '@/assets/icons/SyncDbIcon';
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { IoIosPlay } from 'react-icons/io';
import { Control, useWatch, UseFormSetValue } from 'react-hook-form';
import {
  useManualSyncTelephonyProvider,
  useGetTelephonyProviderSyncLogs,
  useUpdateTelephonyProviderMapping,
  useGetTelephonyProvider
} from 'apis/agency/telephony/hook';

export type NetsapiensDrawerFormValues = {
  businessName: string;
  netsapiensDomain: string;
  syncSchedule: string;
};

interface Props {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  providerId?: string | number;
  businessId?: number;
}

export default function NetsapiensSyncTab({ control, setValue, providerId, businessId }: Props) {
  const theme = useTheme();
  const { data: syncLogsData, isLoading: isLoadingLogs } = useGetTelephonyProviderSyncLogs(providerId, businessId);
  const { mutateAsync: runSync, isPending: isSyncing } = useManualSyncTelephonyProvider(providerId);
  const { mutateAsync: updateMapping } = useUpdateTelephonyProviderMapping(providerId);

  const { data: providerData } = useGetTelephonyProvider(
    providerId,
    { include: 'frequency', telephony_provider: { business_id: businessId } },
    { enabled: Boolean(providerId && businessId) }
  );
  const frequency = providerData?.frequency || (providerData as any)?.data?.frequency;

  const syncSchedule = useWatch({ control, name: 'syncSchedule' });
  const lastSavedScheduleRef = useRef<string | null>(null);

  useEffect(() => {
    if (frequency) {
      setValue('syncSchedule', frequency);
      lastSavedScheduleRef.current = frequency;
    }
  }, [frequency, setValue]);

  useEffect(() => {
    if (syncSchedule && providerId && businessId) {
      if (lastSavedScheduleRef.current === null) {
        lastSavedScheduleRef.current = syncSchedule;
      } else if (lastSavedScheduleRef.current !== syncSchedule) {
        lastSavedScheduleRef.current = syncSchedule;
        updateMapping({
          telephony_provider: {
            business_id: businessId,
            frequency: syncSchedule
          }
        });
      }
    }
  }, [syncSchedule, providerId, businessId, updateMapping]);

  const logsList = syncLogsData?.data || syncLogsData;
  const hasHistory = Array.isArray(logsList) && logsList.length > 0;

  if (!isLoadingLogs && !hasHistory) {
    return (
      <Box
        sx={{
          border: '1px solid #ECECEC',
          borderRadius: '10px',
          p: 6,
          textAlign: 'center',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.light,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}
        >
          <SyncDbIcon />
        </Box>

        <Typography fontSize={20} mb={1} fontWeight={500}>
          Sync Your Records
        </Typography>

        <Typography fontSize={14} color='text.secondary' mb={3} sx={{ maxWidth: 450 }}>
          Start the initial sync to import all data from Netsapiens and link your existing site records.
        </Typography>

        <Button
          startIcon={<IoIosPlay size={16} />}
          onClick={() =>
            providerId &&
            businessId &&
            runSync({ telephony_provider: { business_id: businessId }, sync_type: 'initial' })
          }
          disabled={!providerId || !businessId || isSyncing}
          sx={{
            borderRadius: '20px',
            px: 4,
            minHeight: 40
          }}
        >
          {isSyncing ? 'Starting…' : 'Start Initial Sync'}
        </Button>
      </Box>
    );
  }

  const syncLogs = logsList;

  const columns: ColumnDef<any>[] = [
    {
      header: 'DATE & TIME',
      accessorKey: 'date_time',
      cell: ({ row }) => {
        const item = row.original as any;
        const dateTimeStr = item.date_time || item.started_at || item.created_at || '';
        if (dateTimeStr && (dateTimeStr.includes('T') || dateTimeStr.includes('-'))) {
          try {
            return new Date(dateTimeStr).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          } catch {
            return dateTimeStr;
          }
        }
        return dateTimeStr;
      }
    },
    {
      header: 'STATUS',
      accessorKey: 'status',
      cell: ({ row }) => {
        const status = row.original.status || '';
        const colorMap: Record<string, { text: string; bg: string }> = {
          completed: { text: theme.palette.success.main, bg: theme.palette.success.light },
          failed: { text: theme.palette.error.main, bg: theme.palette.error.light },
          running: { text: theme.palette.primary.main, bg: theme.palette.primary.light }
        };
        const style = colorMap[status] ?? { text: '#374151', bg: '#F3F4F6' };
        const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
        return (
          <Chip
            label={label}
            size='small'
            sx={{ fontWeight: 500, borderRadius: '10px', color: style.text, bgcolor: style.bg }}
          />
        );
      }
    },
    {
      header: 'DURATION',
      accessorKey: 'duration',
      cell: ({ row }) => {
        const item = row.original as any;
        return item.duration || item.total_duration || '—';
      }
    },
    {
      header: 'TOTAL',
      accessorKey: 'total',
      cell: ({ row }) => {
        const item = row.original as any;
        return item.total ?? item.total_synced_count ?? 0;
      }
    },
    {
      header: 'NEW',
      accessorKey: 'new',
      cell: ({ row }) => {
        const item = row.original as any;
        const val = item.new ?? item.metadata?.total_created_count ?? 0;
        return <Typography color='success.main'>{val}</Typography>;
      }
    },
    {
      header: 'UPDATED',
      accessorKey: 'updated',
      cell: ({ row }) => {
        const item = row.original as any;
        const val = item.updated ?? item.metadata?.total_updated_count ?? 0;
        return <Typography color='primary.main'>{val}</Typography>;
      }
    },
    {
      header: 'FAILED',
      accessorKey: 'failed',
      cell: ({ row }) => {
        const item = row.original as any;
        const val = item.failed ?? item.total_failed_count ?? 0;
        return <Typography color='error.main'>{val === 0 ? '—' : val}</Typography>;
      }
    }
  ];

  return (
    <Box>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        bgcolor='#fff'
        borderRadius='8px'
        gap={2}
        mb={3}
        alignItems='stretch'
      >
        <Box
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
          bgcolor={theme.palette.primary.light}
          flex={1}
          padding='20px'
          gap={2}
          borderRadius='10px'
        >
          <Stack direction='row' gap={1.5} alignItems='center'>
            <QuickActionIcon />
            <Stack>
              <Typography fontWeight={400} fontSize={12} lineHeight={1}>
                Quick Action
              </Typography>
              <Typography fontWeight={500} fontSize={16}>
                Sync Now
              </Typography>
            </Stack>
          </Stack>
          <Button
            startIcon={<IoIosPlay size={16} />}
            sx={{ minHeight: 40, flexShrink: 0 }}
            onClick={() =>
              providerId &&
              businessId &&
              runSync({ telephony_provider: { business_id: businessId }, sync_type: 'manual' })
            }
            disabled={!providerId || !businessId || isSyncing}
          >
            {isSyncing ? 'Running…' : 'Run Sync Now'}
          </Button>
        </Box>

        <Box
          display='flex'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          border='1px solid #ECECEC'
          flex={1}
          padding='20px'
          gap={2}
          borderRadius='10px'
        >
          <Stack direction='row' gap={1.5} alignItems='center' minWidth={0}>
            <AutomationIcon />
            <Stack minWidth={0}>
              <Typography fontWeight={400} fontSize={12} lineHeight={1}>
                Automation
              </Typography>
              <Typography fontWeight={500} fontSize={16} noWrap>
                Sync Schedule
              </Typography>
            </Stack>
          </Stack>
          <Box>
            <Select
              label=''
              control={control}
              sortOrder='none'
              name='syncSchedule'
              sx={{ minWidth: 170, height: '45px' }}
              options={[
                { label: 'Every Hour', value: 'hourly' },
                { label: 'Every 6 Hour', value: 'after_six_hours' },
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' }
              ]}
            />
          </Box>
        </Box>
      </Box>

      <Box bgcolor='#fff' padding='16px' borderRadius='8px' border='1px solid #ECECEC'>
        <Typography fontSize={16} fontWeight={500} mb={2}>
          Sync History
        </Typography>
        <DataTable data={syncLogs} columns={columns} containerStyles={{ borderRadius: '4px' }} />
      </Box>
    </Box>
  );
}
